import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import type { AbstractControl, ValidationErrors } from '@angular/forms';
import type { DialogPosition } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';

import type { EnumFeatureFlags } from '../../../user/models/user';
import { CustomValidationValue, LocalStorageItem } from '../../constants';
import type { StorageCodec, StorageEntry, StorageKey, StorageValue, StorageWriteValue } from '../local-storage/local-storage.schema';
import { STORAGE_SCHEMA } from '../local-storage/local-storage.schema';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { CurrentUserService } from '../../../user/services/current-user.service';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  private router = inject(Router);
  dSanitizer = inject(DomSanitizer);
  private localStorageService = inject(LocalStorageService);
  private currentUserService = inject(CurrentUserService);
  private location = inject(Location);

  private _storage?: ReturnType<UtilsService['buildStorage']>;

  // Built once and cached: this is a root singleton, so the accessor object was otherwise
  // reallocated on every read. Reads always go through the cached object, so `this`-based
  // members keep working under destructuring.
  get storage() {
    return (this._storage ??= this.buildStorage());
  }

  private buildStorage() {
    const setItemToLocalStorage = this.setItemToLocalStorage.bind(this);
    const getItemFromLocalStorage = this.getItemFromLocalStorage.bind(this);
    const removeItemFromLocalStorage = this.removeItemFromLocalStorage.bind(this);
    return {
      // Typed schema-backed accessors (#4698). Prefer these over `user`/`token` below.
      get: <K extends StorageKey>(key: K): StorageValue<K> => {
        const entry: StorageEntry<unknown, never> = STORAGE_SCHEMA[key];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- the generic key erases the codec-to-value link that `StorageValue<K>` restores
        return entry.codec.read(getItemFromLocalStorage(entry.item, entry.isGlobal ?? false)) as StorageValue<K>;
      },
      set: <K extends StorageKey>(key: K, value: StorageWriteValue<K>): void => {
        const entry: StorageEntry<unknown, never> = STORAGE_SCHEMA[key];
        // The public signature guarantees `value` matches this key's codec; the
        // generic key erases that link, so narrow the writer at the boundary.
        const codec = entry.codec as StorageCodec<unknown, StorageWriteValue<K>>;
        setItemToLocalStorage(entry.item, codec.write(value), entry.isGlobal ?? false);
      },
      remove: (key: StorageKey): void => {
        const entry: StorageEntry<unknown, never> = STORAGE_SCHEMA[key];
        removeItemFromLocalStorage(entry.item, entry.isGlobal ?? false);
      },
      // Removes rather than writing the codec's `''`: app.component detects a cross-tab
      // logout through `newValue === null` on this key.
      set token(token: string | null) {
        if (!token) {
          removeItemFromLocalStorage(LocalStorageItem.AccessToken, true);
        } else {
          setItemToLocalStorage(LocalStorageItem.AccessToken, token, true);
        }
      },
      get token(): string | null {
        const token = getItemFromLocalStorage(LocalStorageItem.AccessToken, true);
        return typeof token === 'string' ? token : null;
      },
      clearUser: () => {
        removeItemFromLocalStorage(LocalStorageItem.User, true);
      },
      clearToken: () => {
        removeItemFromLocalStorage(LocalStorageItem.AccessToken, true);
        removeItemFromLocalStorage(LocalStorageItem.AuthTime, true);
      },
      clearSearch: () => {
        removeItemFromLocalStorage(LocalStorageItem.WebSearchTreeIds);
        removeItemFromLocalStorage(LocalStorageItem.WebSearchLanguageFilter);
        removeItemFromLocalStorage(LocalStorageItem.WebSearchRangeFilter);
        removeItemFromLocalStorage(LocalStorageItem.WebSearchTypeJSONFilter);
      },
      clearWeekendInCalendar: () => {
        removeItemFromLocalStorage(LocalStorageItem.HideWeekendCalendar);
      },
      clearCompletedTasksInCalendar: () => {
        removeItemFromLocalStorage(LocalStorageItem.ShowCompletedTasks);
      },
      clearReturnUrl: () => {
        removeItemFromLocalStorage(LocalStorageItem.ReturnUrl);
      },
    };
  }

  get navigation() {
    const login = ['/login'];
    const reloadAfterLogout = () => {
      window.location.reload();
    };
    return {
      goToAuthPage: (returnUrl?: string) =>
        returnUrl ? this.router.navigateByUrl(this.location.normalize(returnUrl)) : this.router.navigate(['/']),
      goToUnAuthPage: (returnUrl?: string): Promise<void> =>
        returnUrl
          ? this.router.navigate(login, { queryParams: { returnUrl } }).then(reloadAfterLogout)
          : this.router.navigate(login).then(reloadAfterLogout),
      pBubble: (event: Event, link: string) => {
        event.preventDefault();
        event.stopPropagation();
        const redirectWindow = window.open(link, '_blank', 'noopener,noreferrer');
        if (redirectWindow) {
          redirectWindow.opener = null;
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          redirectWindow.location;
        }
      },
      redirectTo404: (queryParams = {}) => {
        // eslint-disable-next-line no-restricted-syntax -- the 404 page shows only what the caller passes
        void this.router.navigate(['/404'], { queryParams: { ...queryParams }, skipLocationChange: true });
      },
    };
  }

  get validation() {
    return {
      equalGroup:
        (pass: string, cPass: string) =>
        (group: AbstractControl<Record<string, string | null>>): ValidationErrors => {
          const password = group.value[pass];
          const cPassword = group.value[cPass];
          return password === cPassword ? {} : { [CustomValidationValue.MismatchedPasswords]: true };
        },
      isHex:
        () =>
        (control: AbstractControl<string | null>): ValidationErrors => {
          const isHex = /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i;
          return isHex.test(control.value ?? '') ? {} : { [CustomValidationValue.NotValidHex]: true };
        },
    };
  }

  get sanitize() {
    return {
      style: (styles: string) => this.dSanitizer.bypassSecurityTrustStyle(styles),
      url: (url: string) => this.dSanitizer.bypassSecurityTrustUrl(url),
      html: (html: string) => this.dSanitizer.bypassSecurityTrustHtml(html),
      script: (script: string) => this.dSanitizer.bypassSecurityTrustScript(script),
    };
  }

  get tools() {
    return {
      dateHasPassed: (date: Date | string | undefined): boolean => (date === undefined ? false : new Date(date) < new Date()),
      clearHTMLTags: (html = '') => {
        html = html.replace(/<[^>]+>/gm, '');
        // get rid of html-encoded characters:
        html = html.replace(/&nbsp;/gi, ' ');
        html = html.replace(/&amp;/gi, '&');
        html = html.replace(/&quot;/gi, '"');
        html = html.replace(/&lt;/gi, '<');
        html = html.replace(/&gt;/gi, '>');
        return html;
      },
      compareStyles: (str: string) => (str.match(/"[^"]*"|^[^"]*$/g) ?? []).join('').replace(/;|\s+/g, ''),
      removeDupesBy: <T>(key: keyof T, array: T[]): T[] =>
        array.filter((elem, index, self) => self.findIndex(t => t[key] === elem[key]) === index),
      getParameterByName: (name: string, url: string): string => {
        name = name.replace(/[[\]]/g, '\\$&');
        // eslint-disable-next-line security/detect-non-literal-regexp
        const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
          results = regex.exec(url);
        if (!results) {
          return '';
        }
        if (!results[2]) {
          return '';
        }
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
      },
      bytesToMegaBytes: (bytes: number) => bytes / 1048576,
      isTextOverflown: (element: HTMLElement) => element.scrollWidth > element.clientWidth,
      showCount: (value: string | number): string => {
        if (!isNaN(Number(value.toString()))) {
          return `(${value})`;
        } else {
          return '';
        }
      },
      adjustDialogSizeAndPosition: (coords: DOMRect, width = 320, maxHeight = 840) => {
        const windowH = window.innerHeight;
        const position: DialogPosition = {
          left: coords.left > width ? `${coords.left - width}px` : `${coords.left + coords.width}px`,
        };
        if (coords.top + maxHeight < windowH) {
          position.top = `${coords.top}px`;
        } else if (windowH >= maxHeight && coords.top >= maxHeight) {
          position.bottom = `${windowH - coords.top}px`;
        }
        // Leaving both offsets unset is what the old `top: 0` did — MatDialog reads a
        // falsy offset as "centre vertically".

        return {
          position,
          width: `${width}px`,
          maxHeight: `${maxHeight}px`,
        };
      },
    };
  }

  setItemToLocalStorage(key: string, value: string, isGlobal = false): void {
    if (isGlobal) {
      this.localStorageService.setItem(key, value);
    } else {
      this.localStorageService.setCurrentUserItem(key, value);
    }
  }

  getItemFromLocalStorage(key: string, isGlobal = false): unknown {
    return isGlobal ? this.localStorageService.getItem(key) : this.localStorageService.getCurrentUserItem(key);
  }

  removeItemFromLocalStorage(key: string, isGlobal = false): void {
    if (isGlobal) {
      this.localStorageService.removeItem(key);
    } else {
      this.localStorageService.removeCurrentUserItem(key);
    }
  }

  hasFeature(feature: EnumFeatureFlags): boolean {
    const { feature_flags } = this.currentUserService.user.company;
    return feature_flags.map(f => f.feature_flag_type_name).includes(feature);
  }
}
